/**
 * IAN Brand Tokens
 * CSS custom properties + gradient definitions from IAN brand guide.
 * Source: brand_styling/SKILL.md
 */

// ── Primary Brand Colors ──
export const IAN_COLORS = {
  amberGold: '#FFB500',
  amberGoldHover: '#E6A300',
  coral: '#FF5757',
  coralLight: '#FF7070',
  skyBlue: '#13A3E0',
  skyBlueLight: '#47BEF0',
  skyBlueHover: '#0D7BAA',
  skyBlueLightHover: '#7DD1F5',
  green: '#7ED956',
  inkBlack: '#0B111A',
  white: '#FFFFFF',
};

// ── Gradient Definitions ──
export const IAN_GRADIENTS = {
  primary: 'linear-gradient(to right, #FFB500 0%, #FF5757 35%, #13A3E0 80%, #7ED956 100%)',
};

// ── Light Theme Tokens ──
export const LIGHT_TOKENS = {
  bgPrimary: '#FFFFFF',
  bgSecondary: '#F8F9FA',
  bgTertiary: '#F0F1F3',
  bgElevated: '#FFFFFF',
  bgHover: '#F0EDE8',
  bgActive: '#FFF5D6',

  textPrimary: '#0B111A',
  textSecondary: '#4A4F57',
  textTertiary: '#6E747D',
  textDisabled: '#A0A5AD',
  textLink: '#13A3E0',
  textLinkHover: '#0D7BAA',

  borderPrimary: '#E0E2E6',
  borderSecondary: '#C8CBD0',
  borderFocus: '#13A3E0',

  colorSuccess: '#7ED956',
  colorSuccessBg: '#F0FAF0',
  colorWarning: '#FFB500',
  colorWarningBg: '#FFF8E6',
  colorError: '#FF5757',
  colorErrorBg: '#FFF0F0',
  colorInfo: '#13A3E0',
  colorInfoBg: '#EDF7FC',

  shadowSm: '0 1px 2px rgba(11,17,26,0.06)',
  shadowMd: '0 4px 8px rgba(11,17,26,0.08)',
  shadowLg: '0 8px 24px rgba(11,17,26,0.12)',

  overlay: 'rgba(0,0,0,0.3)',
};

// ── Dark Theme Tokens ──
export const DARK_TOKENS = {
  bgPrimary: '#0B111A',
  bgSecondary: '#141C28',
  bgTertiary: '#1C2636',
  bgElevated: '#232F42',
  bgHover: '#2A3750',
  bgActive: '#3D2E00',

  textPrimary: '#F0F1F3',
  textSecondary: '#B0B5BD',
  textTertiary: '#7A808A',
  textDisabled: '#4A4F57',
  textLink: '#47BEF0',
  textLinkHover: '#7DD1F5',

  borderPrimary: '#2A3750',
  borderSecondary: '#3A4A64',
  borderFocus: '#47BEF0',

  colorSuccess: '#7ED956',
  colorSuccessBg: '#0D2B0D',
  colorWarning: '#FFB500',
  colorWarningBg: '#2B2200',
  colorError: '#FF7070',
  colorErrorBg: '#2B0D0D',
  colorInfo: '#47BEF0',
  colorInfoBg: '#0D1F2B',

  shadowSm: '0 1px 2px rgba(0,0,0,0.3)',
  shadowMd: '0 4px 8px rgba(0,0,0,0.4)',
  shadowLg: '0 8px 24px rgba(0,0,0,0.5)',

  overlay: 'rgba(0,0,0,0.5)',
};

// ── Typography ──
export const IAN_TYPOGRAPHY = {
  fontHeading: "'Avenir Next LT Pro', 'Avenir Next', 'Avenir', 'Segoe UI', 'Helvetica Neue', sans-serif",
  fontBody: "'Avenir Next LT Pro', 'Avenir Next', 'Avenir', 'Segoe UI', 'Helvetica Neue', sans-serif",
};

// ── Spacing & Radii ──
export const IAN_SPACING = { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' };
export const IAN_RADII = { sm: '4px', md: '8px', lg: '12px', full: '9999px' };

// ── Component Dimensions ──
export const IAN_DIMENSIONS = {
  headerHeight: '48px',
  gradientBarHeight: '4px',
  cardAccentHeight: '3px',
  quickActionHeight: '56px',
  buttonSmall: '32px',
  buttonMedium: '36px',
};

/**
 * Inject IAN CSS custom properties into :root.
 * Call once at app boot, and again on theme change.
 */
export function injectIanTokens(themeMode = 'light') {
  const tokens = themeMode === 'dark' ? DARK_TOKENS : LIGHT_TOKENS;
  const root = document.documentElement;

  root.style.setProperty('--ian-amber-gold', IAN_COLORS.amberGold);
  root.style.setProperty('--ian-amber-gold-hover', IAN_COLORS.amberGoldHover);
  root.style.setProperty('--ian-coral', IAN_COLORS.coral);
  root.style.setProperty('--ian-sky-blue', IAN_COLORS.skyBlue);
  root.style.setProperty('--ian-green', IAN_COLORS.green);
  root.style.setProperty('--ian-ink-black', IAN_COLORS.inkBlack);
  root.style.setProperty('--ian-gradient-primary', IAN_GRADIENTS.primary);
  root.style.setProperty('--font-heading', IAN_TYPOGRAPHY.fontHeading);
  root.style.setProperty('--font-body', IAN_TYPOGRAPHY.fontBody);

  Object.entries(tokens).forEach(([key, value]) => {
    const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVar, value);
  });
}
